/**
 * SabaSessionsTable — admin view over `/api/admin/saba/sessions`.
 *
 * Simpler than the transactions ledger — sessions are always
 * "created + expired-at" pairs; we compute active/expired against the
 * client clock. Filters: user_id + date range.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { Badge } from "../ui-r1/Badge";
import Button from "../ui-r1/Button";
import Input from "../ui-r1/Input";
import { Card, CardBody } from "../ui-r1/Card";
import { getAdminSabaSessions } from "../../lib/sabaApi";
import { extractError } from "../../lib/formatError";

const PAGE_SIZE = 50;


export default function SabaSessionsTable() {
  const [filters, setFilters] = useState({ user_id: "", from: "", to: "" });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchPage = useCallback(async (opts = {}) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await getAdminSabaSessions({
        ...filters, limit: PAGE_SIZE, offset, ...opts,
      });
      setData(res);
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setOffset(0);
    fetchPage({ offset: 0 });
  };

  const total = data.total || 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <Card data-testid="saba-sess-card">
      <CardBody>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-1">Sessions</div>
            <div className="font-display italic text-2xl text-ink">SABA session log</div>
          </div>
          <div className="text-[10px] tracking-widest uppercase text-ink-3">{total} total</div>
        </div>

        <form
          onSubmit={applyFilters}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4"
          data-testid="saba-sess-filters"
        >
          <Input
            type="text" placeholder="User ID"
            value={filters.user_id}
            onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
            data-testid="saba-sess-filter-user"
          />
          <Input
            type="date" value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            data-testid="saba-sess-filter-from"
          />
          <Input
            type="date" value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            data-testid="saba-sess-filter-to"
          />
          <Button type="submit" variant="secondary" data-testid="saba-sess-apply">
            Apply
          </Button>
        </form>

        <div className="rounded-lg border border-line overflow-x-auto">
          <table className="w-full text-sm" data-testid="saba-sess-table">
            <thead className="bg-highest text-[10px] tracking-widest uppercase text-ink-3">
              <tr>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Currency</th>
                <th className="text-left px-3 py-2">Platform</th>
                <th className="text-left px-3 py-2">Jurisdiction</th>
                <th className="text-left px-3 py-2">Expires</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-8 text-ink-3">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={7} className="text-center py-8 text-danger" data-testid="saba-sess-error">
                  {err}
                </td></tr>
              )}
              {!loading && !err && data.items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-ink-3" data-testid="saba-sess-empty">
                  No active or historical sessions yet.
                </td></tr>
              )}
              {!loading && !err && data.items.map((s) => (
                <SessionRow key={s._id} row={s} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="text-[10px] tracking-widest uppercase text-ink-3">
            {total === 0 ? "0 of 0" : `${from}–${to} of ${total}`}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              data-testid="saba-sess-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              data-testid="saba-sess-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}


function SessionRow({ row }) {
  const isActive = row.expires_at ? new Date(row.expires_at).getTime() > Date.now() : false;
  return (
    <tr
      className="border-t border-line hover:bg-highest/50"
      data-testid={`saba-sess-row-${row._id}`}
    >
      <td className="px-3 py-2 text-ink-2 whitespace-nowrap">{fmtDate(row.created_at)}</td>
      <td className="px-3 py-2 text-ink">
        {row.username || <span className="text-ink-3">—</span>}
      </td>
      <td className="px-3 py-2 text-ink-2">{row.currency}</td>
      <td className="px-3 py-2">
        <Badge variant={row.platform === "mobile" ? "gold" : "muted"}>
          {row.platform}
        </Badge>
      </td>
      <td className="px-3 py-2 text-ink-2">{row.jurisdiction}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="text-ink-2">{fmtDate(row.expires_at)}</span>
        <Badge
          variant={isActive ? "success" : "muted"}
          className="ml-2 !text-[9px]"
        >
          {isActive ? "active" : "expired"}
        </Badge>
      </td>
      <td className="px-3 py-2 text-ink-3 font-mono text-[11px]">
        {row.ip || "—"}
      </td>
    </tr>
  );
}


function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "2-digit", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
